import { Button, Tooltip } from '@heroui/react';
import { Edit, Mail, Send, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ModalConfirmation, Spinner } from '../../../../../components';
import { useHeroToast } from '../../../../../hooks';
import {
  useDeleteUsers,
  useResendWelcomeEmail,
  UserWithHotel,
  useSendPasswordResetLink,
} from '../../../../../queries';

type ActionRowProps = {
  data: UserWithHotel;
};

export const ActionRow = ({ data }: ActionRowProps): React.JSX.Element => {
  const navigate = useNavigate();
  const { mutate: deleteUsers, isPending: isDeletePending } = useDeleteUsers();
  const { mutate: sendPasswordResetLink, isPending: isResendPending } = useSendPasswordResetLink();
  const { mutate: resendWelcomeEmail, isPending: isInvitePending } = useResendWelcomeEmail();

  const { showSuccess, showError } = useHeroToast();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const handleDeleteUser = (): void => {
    setIsModalOpen(false);
    deleteUsers(
      { uids: [data.id] },
      {
        onSuccess: (result) => {
          if (result.failedIds.length > 0) {
            showError('Nie udało się usunąć użytkownika');
            return;
          }
          showSuccess('Użytkownik został usunięty');
        },
        onError: () => showError('Błąd podczas usuwania użytkownika'),
      },
    );
  };

  const handleResend = (): void => {
    setIsResendModalOpen(false);
    sendPasswordResetLink(
      { email: data.email },
      {
        onSuccess: () => showSuccess('User password reset link send success.'),
        onError: (error) => showError(error.message || 'Błąd podczas wysyłania maila'),
      },
    );
  };

  // Ponowne zaproszenie: backend zakłada NOWY token (7 dni) dla konta personelu,
  // a dla administratora wysyła link do ACS — rozpoznaje to po fladze
  // `isAdmin`/`isHotelspotAdmin`, więc nikt nie dostanie linku do obcego produktu.
  const handleResendInvite = (): void => {
    setIsInviteModalOpen(false);
    resendWelcomeEmail(
      { email: data.email },
      {
        onSuccess: () => showSuccess(
          data.isHotelspotAdmin || data.isAdmin
            ? 'Link do ustawienia hasła w ACS został wysłany ponownie'
            : 'Link do ustawienia hasła został wysłany ponownie (ważny 7 dni)',
        ),
        onError: (error) => showError(error.message || 'Błąd podczas wysyłania wiadomości'),
      },
    );
  };

  const isAdminAccount = data.isHotelspotAdmin === true || data.isAdmin === true;

  return (
    <div className="flex items-center justify-end gap-[5px] h-[40px]">
      <ModalConfirmation
        needConfirm={false}
        confirmValue=""
        buttonConfirm="Wyślij"
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onConfirm={handleResendInvite}
        isLoading={isInvitePending}
        title="Wyślij ponownie link do ustawienia hasła"
        description={isAdminAccount
          ? `Czy wysłać ponownie wiadomość na ${data.email}? To konto administratora — dostanie link do ustawienia hasła w ACS.`
          : `Czy wysłać ponownie wiadomość na ${data.email}? Powstanie nowy link do panelu, ważny 7 dni — poprzedni przestanie działać.`}
      />
      <ModalConfirmation
        needConfirm={false}
        confirmValue=""
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDeleteUser}
        isLoading={isDeletePending}
        title="Usuń użytkownika"
        description="Czy na pewno chcesz usunąć tego użytkownika? Operacja jest nieodwracalna — kasuje dokument użytkownika oraz jego konto logowania (Auth)."
      />

      <ModalConfirmation
        needConfirm={false}
        confirmValue=""
        buttonConfirm="Wyślij"
        isOpen={isResendModalOpen}
        onClose={() => setIsResendModalOpen(false)}
        onConfirm={handleResend}
        isLoading={isResendPending}
        title="Wyślij link do zmiany hasła"
        description={`Czy wysłać link do zmiany hasła na ${data.email}? Link jest jednorazowy i wygasa.`}
      />

      <Button isIconOnly size="sm" variant="light" onPress={() => navigate(`/users/${data.id}/edit`)}>
        <Edit size={14} />
      </Button>
      <Button
        isIconOnly
        size="sm"
        variant="light"
        isDisabled={isDeletePending}
        onPress={() => setIsModalOpen(true)}
      >
        {!isDeletePending ? <Trash2 size={14} color="red" /> : <Spinner showLabel={false} />}
      </Button>

      <Tooltip content={isAdminAccount
        ? 'Wyślij ponownie link do ustawienia hasła (ACS)'
        : 'Wyślij ponownie link do ustawienia hasła (7 dni)'}
      >
        <Button
          isIconOnly
          size="sm"
          variant="light"
          isDisabled={isInvitePending}
          onPress={() => setIsInviteModalOpen(true)}
        >
          {isInvitePending ? <Spinner showLabel={false} /> : <Send size={14} color="#1a4d6d" />}
        </Button>
      </Tooltip>

      <Tooltip content="Wyślij link do zmiany hasła">
        <Button
          isIconOnly
          size="sm"
          variant="light"
          isDisabled={isResendPending}
          onPress={() => setIsResendModalOpen(true)}
        >
          {isResendPending ? <Spinner showLabel={false} /> : <Mail size={14} color="orange" />}
        </Button>
      </Tooltip>
    </div>
  );
};
